// Photos and videos sent together show as one album. Each message of an album still travels on
// its own, so apps that do not know albums show the photos one by one, as they always have.

import type { ChatMessage } from "./client";
import type { AlbumMark, Content } from "./content";

/** The most photos and videos one album holds. A larger pick is sent as several albums. */
export const MAX_ALBUM = 10;

type MediaContent = Extract<Content, { type: "media" }>;

/**
 * The chat list's line for an album: "6 photos", "3 videos" or "2 photos and a video". `kinds`
 * are the kinds of the album's messages this device has. When it does not have them all, the
 * line says "photos and videos" rather than guess the mix.
 */
export function albumPreview(count: number, kinds: readonly ("photo" | "video")[]): string {
  if (kinds.length < count) return `${count} photos and videos`;
  const photos = kinds.filter((k) => k === "photo").length;
  const videos = kinds.length - photos;
  if (!videos) return `${count} photos`;
  if (!photos) return `${count} videos`;
  const p = photos === 1 ? "a photo" : `${photos} photos`;
  const v = videos === 1 ? "a video" : `${videos} videos`;
  return `${p} and ${v}`.replace(/^a photo/, "1 photo");
}

/** Album marks for `count` items picked together, split into albums of at most MAX_ALBUM. */
export function albumMarks(count: number, newId: () => string = randomId): (AlbumMark | undefined)[] {
  const out: (AlbumMark | undefined)[] = [];
  for (let start = 0; start < count; start += MAX_ALBUM) {
    const size = Math.min(MAX_ALBUM, count - start);
    // One photo on its own is not an album.
    if (size === 1) {
      out.push(undefined);
      continue;
    }
    const id = newId();
    for (let i = 0; i < size; i++) out.push({ id, index: i, count: size });
  }
  return out;
}

function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

/** A well formed album mark on a photo or video, or undefined. Marks come from other people's apps. */
export function albumOf(content: Content | null | undefined): AlbumMark | undefined {
  if (content?.type !== "media") return undefined;
  if (content.kind !== "photo" && content.kind !== "video") return undefined;
  const a = (content as MediaContent).album;
  if (!a || typeof a.id !== "string" || !a.id || a.id.length > 64) return undefined;
  if (!Number.isInteger(a.index) || !Number.isInteger(a.count)) return undefined;
  if (a.count < 2 || a.count > MAX_ALBUM || a.index < 0 || a.index >= a.count) return undefined;
  return a;
}

export type AlbumGroup<M> = { kind: "single"; message: M } | { kind: "album"; id: string; messages: M[] };

type Groupable = Pick<ChatMessage, "content" | "senderId" | "deleted">;

/**
 * Groups runs of consecutive messages from one sender that share an album id. A message of an
 * album that is deleted, or that another message landed between, shows on its own or starts a new
 * run. Within a run the photos are put in album order. A run of one is shown as a single message.
 */
export function groupAlbums<M extends Groupable>(messages: readonly M[]): AlbumGroup<M>[] {
  const out: AlbumGroup<M>[] = [];
  let run: M[] = [];
  let runId: string | null = null;
  let runSender: string | null = null;
  const seen = new Set<number>();

  const flush = () => {
    if (run.length === 1) out.push({ kind: "single", message: run[0] as M });
    else if (run.length > 1) {
      const sorted = [...run].sort(
        (a, b) => (albumOf(a.content)?.index ?? 0) - (albumOf(b.content)?.index ?? 0),
      );
      out.push({ kind: "album", id: runId as string, messages: sorted });
    }
    run = [];
    runId = null;
    runSender = null;
    seen.clear();
  };

  for (const m of messages) {
    const mark = m.deleted ? undefined : albumOf(m.content);
    if (!mark) {
      flush();
      out.push({ kind: "single", message: m });
      continue;
    }
    // The same place twice means a second album that reuses the id. Start again.
    if (mark.id !== runId || m.senderId !== runSender || seen.has(mark.index)) flush();
    run.push(m);
    runId = mark.id;
    runSender = m.senderId;
    seen.add(mark.index);
    if (run.length === mark.count) flush();
  }
  flush();
  return out;
}
